/**
 * Pricing Options — inspector.
 *
 * Two kinds of setting, and the split is deliberate: how the chooser looks is
 * this block's own business, while the options it lists belong to the table
 * and are written back through `setTableAttributes`. An author does not have
 * to know that — they click the control and edit what they see.
 */
import { __, sprintf } from '@wordpress/i18n';
import {
  PanelBody,
  SelectControl,
  TextControl,
  BaseControl,
  Button,
  __experimentalVStack as VStack,
} from '@wordpress/components';
import { ColorPalette, useSettings } from '@wordpress/block-editor';

import { MAX_PRICING_OPTIONS, emptyOption } from '../../_shared/pricing-defaults';
import { fromColorPreset, toColorPreset } from '../../../utils/helpers';

const LAYOUT_OPTIONS = [
  { label: __('Segmented', 'brandy-blocks'), value: 'segmented' },
  { label: __('List', 'brandy-blocks'), value: 'list' },
  { label: __('Cards', 'brandy-blocks'), value: 'cards' },
];

const ALIGN_OPTIONS = [
  { label: __('Left', 'brandy-blocks'), value: 'left' },
  { label: __('Center', 'brandy-blocks'), value: 'center' },
  { label: __('Right', 'brandy-blocks'), value: 'right' },
];

const SHAPE_OPTIONS = [
  { label: __('Pill', 'brandy-blocks'), value: 'pill' },
  { label: __('Rounded', 'brandy-blocks'), value: 'rounded' },
  { label: __('Square', 'brandy-blocks'), value: 'square' },
];

export default function OptionsSettings({
  attributes,
  setAttributes,
  options,
  defaultOption,
  setTableAttributes,
}) {
  const { optionLayout, optionAlign, shape, accentColor, activeTextColor, heading } =
    attributes;

  const [palette] = useSettings('color.palette');

  const updateOption = (index, patch) =>
    setTableAttributes({
      terms: options.map((option, i) =>
        i === index ? { ...option, ...patch } : option
      ),
    });

  const addOption = () =>
    setTableAttributes({ terms: [...options, emptyOption()] });

  /*
   * Removing the option a visitor lands on would leave the table pointing past
   * the end of its own list, so the default follows the removal down.
   */
  const removeOption = (index) =>
    setTableAttributes({
      terms: options.filter((_, i) => i !== index),
      defaultTerm: Math.max(0, Math.min(options.length - 2, defaultOption)),
    });

  return (
    <>
      <PanelBody title={__('Chooser', 'brandy-blocks')}>
        <SelectControl
          label={__('Layout', 'brandy-blocks')}
          value={optionLayout || 'segmented'}
          options={LAYOUT_OPTIONS}
          onChange={(value) => setAttributes({ optionLayout: value })}
          help={__(
            'Segmented suits two or three options in a row; list and cards stay readable however many there are.',
            'brandy-blocks'
          )}
        />

        <TextControl
          label={__('Heading', 'brandy-blocks')}
          value={heading}
          onChange={(value) => setAttributes({ heading: value })}
          help={__('Sits above the options. Leave empty to hide it.', 'brandy-blocks')}
        />

        <SelectControl
          label={__('Alignment', 'brandy-blocks')}
          value={optionAlign || 'center'}
          options={ALIGN_OPTIONS}
          onChange={(value) => setAttributes({ optionAlign: value })}
        />

        <SelectControl
          label={__('Shape', 'brandy-blocks')}
          value={shape || 'pill'}
          options={SHAPE_OPTIONS}
          onChange={(value) => setAttributes({ shape: value })}
        />

        {/*
         * One colour is the whole control: it paints the option the reader has
         * chosen, and everything behind it stays a neutral mix of the
         * surrounding text so the two can never clash. The second is offered
         * only because the first can create the problem it solves — a dark
         * selection needs a light label, and nothing can guess that.
         */}
        <BaseControl
          __nextHasNoMarginBottom
          label={__('Selected colour', 'brandy-blocks')}
          help={__('Leave empty for the theme’s default.', 'brandy-blocks')}
        >
          {/* Stored as `var:preset|color|<slug>` for palette picks — render.php
              already expands those through Helpers::get_ref_color(). */}
          <ColorPalette
            colors={palette || []}
            value={fromColorPreset(accentColor, palette)}
            onChange={(value) =>
              setAttributes({ accentColor: toColorPreset(value, palette) || '' })
            }
            clearable
            __experimentalIsRenderedInSidebar
          />
        </BaseControl>

        {accentColor && (
          <BaseControl
            __nextHasNoMarginBottom
            label={__('Selected label colour', 'brandy-blocks')}
            help={__(
              'Only needed if the colour above is dark enough to swallow the label.',
              'brandy-blocks'
            )}
          >
            <ColorPalette
              colors={palette || []}
              value={fromColorPreset(activeTextColor, palette)}
              onChange={(value) =>
                setAttributes({
                  activeTextColor: toColorPreset(value, palette) || '',
                })
              }
              clearable
              __experimentalIsRenderedInSidebar
            />
          </BaseControl>
        )}
      </PanelBody>

      <PanelBody title={__('Options', 'brandy-blocks')}>
        <VStack spacing={4}>
          {options.map((option, index) => (
            <BaseControl
              key={index}
              __nextHasNoMarginBottom
              label={sprintf(
                /* translators: %d: position of the option in the list. */
                __('Option %d', 'brandy-blocks'),
                index + 1
              )}
            >
              <TextControl
                label={__('Label', 'brandy-blocks')}
                value={option.label}
                onChange={(value) => updateOption(index, { label: value })}
              />
              <TextControl
                label={__('Badge', 'brandy-blocks')}
                value={option.badge}
                onChange={(value) => updateOption(index, { badge: value })}
                help={__(
                  'A small pill beside the label — "Save 20%", say.',
                  'brandy-blocks'
                )}
              />
              {options.length > 2 && (
                <Button
                  isDestructive
                  variant="tertiary"
                  onClick={() => removeOption(index)}
                >
                  {__('Remove option', 'brandy-blocks')}
                </Button>
              )}
            </BaseControl>
          ))}

          {options.length < MAX_PRICING_OPTIONS && (
            <Button variant="secondary" onClick={addOption}>
              {__('Add option', 'brandy-blocks')}
            </Button>
          )}

          <SelectControl
            label={__('Visitors start on', 'brandy-blocks')}
            value={String(defaultOption)}
            options={options.map((option, index) => ({
              label: option.label || String(index + 1),
              value: String(index),
            }))}
            onChange={(value) =>
              setTableAttributes({ defaultTerm: parseInt(value, 10) })
            }
            help={__(
              'Independent of whichever option you click on the canvas.',
              'brandy-blocks'
            )}
          />
        </VStack>
      </PanelBody>
    </>
  );
}
